import React, { useEffect, useState, useRef, useMemo } from "react";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import moment from "moment";
import "./App.css";
import NotificationSettings from "./components/NotificationSettings";
import NotificationPrompt from "./components/NotificationPrompt";
// Import is kept but component is disabled in the JSX
import InstallPrompt from "./components/InstallPrompt";
import RecurringEventEditModal from "./components/RecurringEventEditModal";

const localizer = momentLocalizer(moment);

// Base API URL - use local in development, Railway in production
export const BASE_API_URL = process.env.NODE_ENV === 'production' 
  ? 'https://api.promptlyyou.com'
  : 'http://localhost:5000';

// All available views
const allViews = [Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA];

// Google Calendar color mapping (colorId to hex color)
// Based on the standard Google Calendar colors
const GOOGLE_CALENDAR_COLORS = {
  "1": "#7986cb", // Lavender
  "2": "#33b679", // Sage
  "3": "#8e24aa", // Grape
  "4": "#ef6c00", // Flamingo - Fixed color assignment
  "5": "#f6bf26", // Banana - Fixed color assignment
  "6": "#f4511e", // Tangerine
  "7": "#039be5", // Peacock
  "8": "#616161", // Graphite
  "9": "#3f51b5", // Blueberry
  "10": "#0b8043", // Basil
  "11": "#d50000", // Tomato
  // Add more colors as needed
  // Default Google Calendar color is a light blue
  "default": "#4285F4"
};

// Function to check if an event is recurring
const isRecurringEvent = (event) => {
  // Return false if event is null or undefined
  if (!event) return false;
  
  // Check if the event has recurrence information
  // 1. Check for 'recurrence' array - used for the master recurring event
  const hasRecurrenceRules = Array.isArray(event.recurrence) && event.recurrence.length > 0;
  
  // 2. Check for 'recurringEventId' - used for instances of a recurring event
  const isRecurringInstance = typeof event.recurringEventId === 'string' && 
                             event.recurringEventId.trim() !== '';
  
  // 3. Check for simple 'repeat' flag - may be used in some cases
  const hasRepeatFlag = event.recurrence === 'repeat';

  // Get the recurrence rules in a readable format if they exist
  const recurrenceDetails = hasRecurrenceRules ? event.recurrence.map(rule => {
    // Parse RRULE if it exists
    if (rule.startsWith('RRULE:')) {
      const ruleComponents = rule.substring(6).split(';').reduce((acc, part) => {
        const [key, value] = part.split('=');
        acc[key] = value;
        return acc;
      }, {});
      return { type: 'RRULE', components: ruleComponents };
    }
    return { type: 'unknown', rule };
  }) : [];
  
  // Enhanced debug logging
  console.log(`Event ${event.id || 'new'} recurring check:`, { 
    eventId: event.id,
    title: event.title,
    hasRecurrenceRules, 
    isRecurringInstance,
    recurringEventId: event.recurringEventId, 
    hasRepeatFlag,
    recurrenceRules: event.recurrence,
    parsedRules: recurrenceDetails,
    isRecurring: hasRecurrenceRules || isRecurringInstance || hasRepeatFlag
  });
  
  // For instances, we don't look at hasRecurrenceRules since it won't have them
  // Instead, we just need to check if it's an instance (has recurringEventId)
  return isRecurringInstance || hasRecurrenceRules || hasRepeatFlag;
};

// App version - increment this when you make changes that require cache refresh
const APP_VERSION = '1.0.4'; // Updated for Flamingo color fix

// Expose APP_VERSION to the service worker
window.APP_VERSION = APP_VERSION;

// Function to create a new event
const createEvent = async (eventData) => {
  try {
    const response = await fetch(`${BASE_API_URL}/api/events`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      body: JSON.stringify(eventData),
      credentials: 'include', // Include cookies
    });
    
    if (response.status === 401) {
      const data = await response.json();
      window.location.href = data.loginUrl;
      throw new Error("Authentication required");
    }
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `HTTP error ${response.status}: ${errorData.error || response.statusText}`
      );
    }
    
    return await response.json();
  } catch (error) {
    console.error("Error creating event:", error);
    throw error;
  }
};

// Function to update an existing event
const updateEvent = async (eventId, eventData, recurringEditType = null) => {
  try {
    // Add the recurring edit type to the API request if specified
    const requestData = { ...eventData };
    if (recurringEditType) {
      requestData.recurringEditType = recurringEditType;
    }
    
    const response = await fetch(`${BASE_API_URL}/api/events/${eventId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      body: JSON.stringify(requestData),
      credentials: 'include', // Include cookies
    });
    
    if (response.status === 401) {
      const data = await response.json();
      window.location.href = data.loginUrl;
      throw new Error("Authentication required");
    }
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `HTTP error ${response.status}: ${errorData.error || response.statusText}`
      );
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error updating event ${eventId}:`, error);
    throw error;
  }
};

// Function to delete an event
const deleteEvent = async (eventId, deletionType = 'single') => {
  try {
    console.log('Deleting event:', eventId, 'with type:', deletionType);
    
    // Add the deletion type as a query parameter
    const queryParams = new URLSearchParams();
    if (deletionType !== 'single') {
      queryParams.append('type', deletionType);
    }
    
    const response = await fetch(
      `${BASE_API_URL}/api/events/${eventId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`, 
      {
        method: "DELETE",
        credentials: 'include',
      }
    );
    
    if (response.status === 401) {
      const data = await response.json();
      window.location.href = data.loginUrl;
      throw new Error("Authentication required");
    }
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `HTTP error ${response.status}: ${errorData.error || response.statusText}`
      );
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error deleting event ${eventId}:`, error);
    throw error;
  }
};

// Login component for unauthorized users
const LoginPanel = ({ onLogin }) => {
  return (
    <div style={{
      textAlign: 'center',
      padding: '50px 20px',
      maxWidth: '600px',
      margin: '80px auto',
      backgroundColor: '#0B1420',
      borderRadius: '16px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    }}>
      <img 
        src="/PromptlyYou_Logo.png" 
        alt="PromptlyYou Logo" 
        style={{
          height: "80px",
          marginBottom: "12px"
        }}
      />
      <h2 style={{ 
        marginBottom: '24px', 
        color: '#ffffff', 
        fontSize: '28px',
        fontWeight: '700'
      }}>Welcome to PromptlyYou!</h2>
      <p style={{ 
        marginBottom: '36px', 
        fontSize: '16px', 
        color: '#a0aec0',
        lineHeight: '1.6'
      }}>
        Please login with your Google account to access your calendar.
      </p>
      <a 
        href={`${BASE_API_URL}/api/direct-login`}
        style={{
          display: 'inline-block',
          padding: '14px 28px',
          backgroundColor: '#4285F4',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          cursor: 'pointer',
          fontWeight: 'bold',
          boxShadow: '0 4px 12px rgba(66, 133, 244, 0.2)',
          textDecoration: 'none',
          transition: 'all 0.3s ease'
        }}
        onMouseOver={(e) => {
          e.target.style.backgroundColor = '#3367d6';
          e.target.style.boxShadow = '0 6px 16px rgba(66, 133, 244, 0.4)';
        }}
        onMouseOut={(e) => {
          e.target.style.backgroundColor = '#4285F4';
          e.target.style.boxShadow = '0 4px 12px rgba(66, 133, 244, 0.2)';
        }}
      >
        Login with Google
      </a>
      <div style={{ marginTop: '20px', fontSize: '14px', color: '#718096' }}>
        <p>Having trouble? Try clicking the button directly or refreshing the page.</p>
      </div>
    </div>
  );
};

// Custom event component that applies the Google Calendar colors
const EventComponent = ({ event }) => {
  // Get color based on the colorId or use default
  const backgroundColor = event.colorId 
    ? GOOGLE_CALENDAR_COLORS[event.colorId] || GOOGLE_CALENDAR_COLORS.default
    : GOOGLE_CALENDAR_COLORS.default;
    
  const getTextColor = (bgColor) => {
    const hex = bgColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    const brightness = (r * 0.299 + g * 0.587 + b * 0.114);
    return brightness > 186 ? '#000000' : '#ffffff';
  };
  
  const textColor = getTextColor(backgroundColor);

  const mobileStyles = `
    @media (max-width: 768px) {
      .rbc-event {
        padding: 1px !important;
        margin: 0 !important;
      }
      .rbc-event-content {
        font-size: 9px !important;
        line-height: 1.1 !important;
      }
      .rbc-row {
        min-height: 35px !important;
      }
      .rbc-date-cell {
        font-size: 11px !important;
        padding: 1px !important;
      }
      .rbc-header {
        font-size: 11px !important;
        padding: 3px 1px !important;
      }
      .rbc-toolbar button {
        font-size: 11px !important;
        padding: 5px 8px !important;
      }
      .rbc-toolbar-label {
        font-size: 13px !important;
      }
      .rbc-agenda-view table.rbc-agenda-table tbody > tr > td {
        font-size: 11px !important;
        padding: 3px !important;
      }
      .rbc-agenda-view table.rbc-agenda-table .rbc-agenda-time-cell {
        font-size: 10px !important;
      }
      .rbc-time-view .rbc-time-header-content .rbc-header {
        font-size: 10px !important;
      }
      .rbc-time-view .rbc-time-content > * + * > * {
        font-size: 10px !important;
      }
      .rbc-time-view .rbc-events-container .rbc-event-content {
        font-size: 10px !important;
      }
    }
  `;
  
  return (
    <>
      <style>{mobileStyles}</style>
      <div style={{ 
        backgroundColor,
        color: textColor,
        height: '100%',
        width: '100%',
        padding: '2px 4px',
        borderRadius: '3px',
        fontSize: '90%',
        fontWeight: '500',
        boxShadow: event.done
          ? '0 0 0 2px #63b3ed, 0 1px 2px rgba(0,0,0,0.1)'
          : '0 1px 2px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
        {event.title}
      </div>
    </>
  );
};

// Custom toolbar component
const CustomToolbar = ({ onView, onNavigate, label, view }) => {
  const goToBack = () => {
    onNavigate('PREV');
  };

  const goToNext = () => {
    onNavigate('NEXT');
  };

  const goToToday = () => {
    onNavigate('TODAY');
  };

  // Button styles
  const btnStyle = {
    padding: '8px 14px',
    margin: '0 4px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#f5f7fa',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#4a5568',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  };

  const activeStyle = {
    ...btnStyle,
    backgroundColor: '#4285F4',
    color: 'white',
    boxShadow: '0 2px 4px rgba(66,133,244,0.3)'
  };

  return (
    <div style={{ 
      marginBottom: 16, 
      display: 'flex', 
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '10px',
      backgroundColor: 'white',
      padding: '12px 16px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
    }}>
      {/* Left section - Navigation */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <button 
          style={btnStyle} 
          onClick={goToBack}>
          &lt;
        </button>
        <button 
          style={{
            ...btnStyle,
            fontWeight: '600',
            backgroundColor: '#edf2ff'
          }} 
          onClick={goToToday}>
          Today
        </button>
        <button 
          style={btnStyle} 
          onClick={goToNext}>
          &gt;
        </button>
        <span style={{ 
          marginLeft: 14, 
          fontWeight: 'bold',
          fontSize: '16px',
          color: '#2d3748'
        }}>
          {label}
        </span>
      </div>

      {/* Center section - View options */}
      <div style={{
        display: 'flex',
        background: '#f5f7fa',
        borderRadius: '10px',
        padding: '3px',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
      }}>
        <button 
          style={view === Views.MONTH ? activeStyle : btnStyle} 
          onClick={() => onView(Views.MONTH)}>
          Month
        </button>
        <button 
          style={view === Views.WEEK ? activeStyle : btnStyle} 
          onClick={() => onView(Views.WEEK)}>
          Week
        </button>
        <button 
          style={view === Views.DAY ? activeStyle : btnStyle} 
          onClick={() => onView(Views.DAY)}>
          Day
        </button>
        <button 
          style={view === Views.AGENDA ? activeStyle : btnStyle} 
          onClick={() => onView(Views.AGENDA)}>
          Agenda
        </button>
      </div>
    </div>
  );
};

// Parse Google Calendar RRULE to frontend format
const parseRecurrenceRule = (recurrence) => {
  try {
    if (!Array.isArray(recurrence) || recurrence.length === 0) {
      return {
        recurrence: 'none',
        frequency: 'daily',
        interval: 1,
        until: ''
      };
    }

    const rrule = recurrence[0];
    
    if (!rrule.startsWith('RRULE:')) {
      return {
        recurrence: 'none',
        frequency: 'daily',
        interval: 1,
        until: ''
      };
    }

    // Parse the RRULE components
    const rules = rrule.substring(6).split(';').reduce((acc, rule) => {
      const [key, value] = rule.split('=');
      acc[key.toLowerCase()] = value;
      return acc;
    }, {});

    return {
      recurrence: 'repeat',
      frequency: rules.freq?.toLowerCase() || 'daily',
      interval: parseInt(rules.interval, 10) || 1,
      until: rules.until ? moment(rules.until.substring(0, 8), 'YYYYMMDD').format('YYYY-MM-DD') : ''
    };
  } catch (error) {
    console.error('Error parsing recurrence rule:', error);
    return {
      recurrence: 'none',
      frequency: 'daily',
      interval: 1,
      until: ''
    };
  }
};

// Event Form Component
const EventForm = ({ event, onSave, onCancel, onDelete, slotInfo }) => {
  // State to store master event recurrence rules
  const [masterEventRules, setMasterEventRules] = useState(null);

  // Fetch master event details if this is an instance
  useEffect(() => {
    const fetchMasterEvent = async () => {
      if (event?.recurringEventId) {
        console.log('Fetching master event for instance:', event.recurringEventId);
        try {
          const response = await fetch(`${BASE_API_URL}/api/events/${event.recurringEventId}`, {
            credentials: 'include'
          });
          if (response.ok) {
            const masterEvent = await response.json();
            console.log('Master event fetched:', masterEvent);
            if (masterEvent.recurrence) {
              setMasterEventRules(masterEvent.recurrence);
            } else {
              console.warn('Master event has no recurrence rules:', masterEvent);
            }
          } else if (response.status === 404) {
            console.warn('Master event not found - it may have been deleted');
            setMasterEventRules(null);
          } else {
            console.error('Failed to fetch master event:', response.status);
          }
        } catch (error) {
          console.error('Error fetching master event:', error);
        }
      } else if (event?.recurrence) {
        // If this is a master event, use its own recurrence rules
        setMasterEventRules(event.recurrence);
      }
    };
    fetchMasterEvent();
  }, [event]);

  // Debug: Log recurring event information
  useEffect(() => {
    if (event) {
      const isRecurringInstance = !!event.recurringEventId;
      const hasRecurrenceRules = Array.isArray(event.recurrence) && event.recurrence.length > 0;

      console.log('Event Form Details:', {
        id: event.id,
        title: event.title,
        hasRecurrence: hasRecurrenceRules,
        isInstance: isRecurringInstance,
        masterEventId: event.recurringEventId,
        masterEventRules,
        recurrenceRules: event.recurrence,
        parsed: hasRecurrenceRules ? parseRecurrenceRule(event.recurrence) : null
      });
    }
  }, [event, masterEventRules]);

  // Get recurrence settings from event or master event
  const recurrenceSettings = useMemo(() => {
    if (event?.recurringEventId) {
      // This is an instance
      if (masterEventRules) {
        // Use master event rules if available
        console.log('Using master event rules for instance:', masterEventRules);
        return parseRecurrenceRule(masterEventRules);
      }
      // If master event is not available yet, show as recurring but without details
      console.log('Master event rules not loaded yet, showing as recurring');
      return {
        recurrence: 'repeat',
        frequency: 'daily',
        interval: 1,
        until: ''
      };
    } else if (event?.recurrence) {
      // This is a master event with its own recurrence rules
      console.log('Using event\'s own recurrence rules:', event.recurrence);
      return parseRecurrenceRule(event.recurrence);
    }
    // Not a recurring event
    console.log('No recurrence rules found, using defaults');
    return {
      recurrence: 'none',
      frequency: 'daily',
      interval: 1,
      until: ''
    };
  }, [event, masterEventRules]);
  
  const [formData, setFormData] = useState({
    title: event ? event.title : '',
    start: event ? formatDateTimeForInput(event.start) : 
           slotInfo ? formatDateTimeForInput(slotInfo.start) : '',
    end: event ? formatDateTimeForInput(event.end) : 
         slotInfo ? formatDateTimeForInput(slotInfo.end) : '',
    description: event ? event.description || '' : '',
    location: event ? event.location || '' : '',
    colorId: event ? event.colorId || '4' : '4',
    // Use parsed recurrence settings
    recurrence: recurrenceSettings.recurrence,
    frequency: recurrenceSettings.frequency,
    interval: recurrenceSettings.interval,
    until: recurrenceSettings.until,
    // Notification settings - ensure proper boolean and number types
    notifications_enabled: event ? Boolean(event.notifications_enabled !== undefined ? event.notifications_enabled : true) : true,
    minutes_before: event ? Number(event.minutes_before !== undefined ? event.minutes_before : 15) : 15,
    repeat_notifications: event ? Boolean(event.repeat_notifications !== undefined ? event.repeat_notifications : false) : false,
    repeat_interval: event ? Number(event.repeat_interval !== undefined ? event.repeat_interval : 15) : 15,
    done: event ? (Boolean(event.done !== undefined ? event.done : false) && moment(event.updated_at).format('YYYY-MM-DD') === moment(event.start).format('YYYY-MM-DD')) : false,
    //done: event ? Boolean(event.done !== undefined ? event.done : false) : false
    // Attendees
    attendees: event && event.attendees ? event.attendees : []
  });
  
  // State for managing the guest email input
  const [guestEmail, setGuestEmail] = useState('');
  
  // State for current user's email
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  
  // Fetch current user's email when component mounts
  useEffect(() => {
    const fetchUserEmail = async () => {
      try {
        const response = await fetch(`${BASE_API_URL}/api/user-info`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userInfo = await response.json();
          setCurrentUserEmail(userInfo.email);
        }
      } catch (error) {
        console.error('Error fetching user email:', error);
      }
    };
    fetchUserEmail();
  }, []);
  
  // Add effect to close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      const dropdown = document.getElementById('colorDropdown');
      const colorSelector = document.getElementById('colorSelector');
      
      if (dropdown && colorSelector && 
          !colorSelector.contains(event.target) && 
          !dropdown.contains(event.target)) {
        dropdown.style.display = 'none';
      }
    }
    
    // Function to position dropdown based on available space
    function positionDropdown() {
      const dropdown = document.getElementById('colorDropdown');
      // Get dimensions and positions
      const selectorRect = document.getElementById('colorSelector').getBoundingClientRect();
      
      if (!dropdown) return;
      
      // Get available space
      const spaceBelow = window.innerHeight - selectorRect.bottom;
      const spaceAbove = selectorRect.top;
      
      // Calculate minimum required height
      const minHeight = 11 * 40; // 11 items * ~40px height per item
      const dropdownHeight = Math.min(minHeight, 300); // cap at 300px
      
      // Position the dropdown
      if (spaceBelow >= dropdownHeight + 10) {
        // Show below - enough space
        dropdown.style.top = `${selectorRect.bottom}px`;
        dropdown.style.bottom = 'auto';
        dropdown.style.left = `${selectorRect.left}px`;
        dropdown.style.maxHeight = `${Math.min(spaceBelow - 20, 300)}px`;
      } else if (spaceAbove >= dropdownHeight + 10) {
        // Show above - not enough space below but enough above
        dropdown.style.bottom = `${window.innerHeight - selectorRect.top}px`;
        dropdown.style.top = 'auto';
        dropdown.style.left = `${selectorRect.left}px`;
        dropdown.style.maxHeight = `${Math.min(spaceAbove - 20, 300)}px`;
      } else {
        // Not enough space either way - center and make it smaller
        dropdown.style.top = `${Math.max(10, window.innerHeight/2 - 150)}px`;
        dropdown.style.left = `${selectorRect.left}px`;
        dropdown.style.maxHeight = `${Math.min(window.innerHeight - 40, 300)}px`;
      }
    }
    
    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', positionDropdown);
    
    // Initial positioning
    setTimeout(positionDropdown, 10);
    
    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', positionDropdown);
    };
  }, []);
  
  // Format date object to datetime-local input format (YYYY-MM-DDTHH:MM)
  function formatDateTimeForInput(date) {
    if (!date) return '';
    // Create a moment object and keep it in local time
    return moment(date).format('YYYY-MM-DDTHH:mm');
  }
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Convert values to appropriate types
    let newValue = value;
    if (name === 'notifications_enabled' || name === 'repeat_notifications' || name === 'done') {
      newValue = Boolean(value);
    } else if (name === 'minutes_before' || name === 'repeat_interval') {
      newValue = Number(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };
  
  // Handler for adding a guest email
  const handleAddGuest = () => {
    if (!guestEmail) return;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(guestEmail)) {
      alert('Please enter a valid email address');
      return;
    }
    
    // Check for duplicates
    if (formData.attendees.some(a => a.email === guestEmail)) {
      alert('This email has already been added');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      attendees: [...prev.attendees, { email: guestEmail, responseStatus: 'needsAction' }]
    }));
    setGuestEmail('');
  };
  
  // Handler for removing a guest
  const handleRemoveGuest = (emailToRemove) => {
    setFormData(prev => ({
      ...prev,
      attendees: prev.attendees.filter(a => a.email !== emailToRemove)
    }));
  };
  
  // Handler for updating attendee response status
  const handleUpdateResponse = async (responseStatus) => {
    if (!event || !event.id) return;
    
    try {
      const response = await fetch(`${BASE_API_URL}/api/events/${event.id}/response`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ responseStatus })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update response');
      }
      
      // Update local state to reflect the change
      setFormData(prev => ({
        ...prev,
        attendees: prev.attendees.map(a => 
          a.email?.toLowerCase() === currentUserEmail?.toLowerCase()
            ? { ...a, responseStatus }
            : a
        )
      }));
      
      // Refresh the calendar
      window.location.reload();
    } catch (error) {
      console.error('Error updating response:', error);
    }
  };
  
  // Allow adding guest on Enter key
  const handleGuestEmailKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddGuest();
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create a base object with only the changed fields
    const apiData = {};
    
    // For new events, include all fields
    if (!event) {
      apiData.title = formData.title;
      apiData.start = moment(formData.start).format('YYYY-MM-DDTHH:mm:ss');
      apiData.end = moment(formData.end).format('YYYY-MM-DDTHH:mm:ss');
      apiData.description = formData.description;
      apiData.location = formData.location;
      apiData.colorId = formData.colorId;
      
      // Add notification settings with proper types
      apiData.notifications_enabled = Boolean(formData.notifications_enabled);
      apiData.minutes_before = Number(formData.minutes_before);
      apiData.repeat_notifications = Boolean(formData.repeat_notifications);
      apiData.repeat_interval = Number(formData.repeat_interval);
      apiData.done = Boolean(formData.done);
      
      // Add attendees (send only emails to backend)
      apiData.attendees = formData.attendees.map(a => a.email || a);
      
      // Add recurrence rules if enabled
      if (formData.recurrence === 'repeat') {
        let rrule = `RRULE:FREQ=${formData.frequency.toUpperCase()};INTERVAL=${formData.interval}`;
        // Only add UNTIL if an end date is specified
        if (formData.until) {
          const untilDate = moment(formData.until).format('YYYYMMDD');
          rrule += `;UNTIL=${untilDate}T235959Z`;
        }
        apiData.recurrence = [rrule];
      }
    } else {
      // For existing events, only include fields that have changed
      if (formData.title !== event.title) apiData.title = formData.title;
      if (formData.description !== event.description) apiData.description = formData.description;
      if (formData.location !== event.location) apiData.location = formData.location;
      if (formData.colorId !== event.colorId) apiData.colorId = formData.colorId;
      
      // Only include dates if they've changed and are valid
      const startChanged = moment(formData.start).format('YYYY-MM-DDTHH:mm:ss') !== 
                          moment(event.start).format('YYYY-MM-DDTHH:mm:ss');
      const endChanged = moment(formData.end).format('YYYY-MM-DDTHH:mm:ss') !== 
                        moment(event.end).format('YYYY-MM-DDTHH:mm:ss');
      
      if (startChanged) apiData.start = moment(formData.start).format('YYYY-MM-DDTHH:mm:ss');
      if (endChanged) apiData.end = moment(formData.end).format('YYYY-MM-DDTHH:mm:ss');
      
      // Add notification settings if they've changed - ensure proper type comparison
      if (Boolean(formData.notifications_enabled) !== Boolean(event.notifications_enabled)) {
        apiData.notifications_enabled = Boolean(formData.notifications_enabled);
      }
      if (Number(formData.minutes_before) !== Number(event.minutes_before)) {
        apiData.minutes_before = Number(formData.minutes_before);
      }
      if (Boolean(formData.repeat_notifications) !== Boolean(event.repeat_notifications)) {
        apiData.repeat_notifications = Boolean(formData.repeat_notifications);
      }
      if (Number(formData.repeat_interval) !== Number(event.repeat_interval)) {
        apiData.repeat_interval = Number(formData.repeat_interval);
      }
      if (Boolean(formData.done) !== Boolean(event.done)) {
        apiData.done = Boolean(formData.done);
      }
      
      // Add recurrence rules if enabled or changed
      if (formData.recurrence === 'repeat') {
        let rrule = `RRULE:FREQ=${formData.frequency.toUpperCase()};INTERVAL=${formData.interval}`;
        // Only add UNTIL if an end date is specified
        if (formData.until) {
          const untilDate = moment(formData.until).format('YYYYMMDD');
          rrule += `;UNTIL=${untilDate}T235959Z`;
        }
        apiData.recurrence = [rrule];
      } else if (event.recurrence && formData.recurrence === 'none') {
        // If recurrence was disabled, send empty array to clear it
        apiData.recurrence = [];
      }
      
      // Add attendees if they've changed
      const eventAttendees = event.attendees ? event.attendees.map(a => a.email || a).sort() : [];
      const formAttendees = formData.attendees.map(a => a.email || a).sort();
      if (JSON.stringify(eventAttendees) !== JSON.stringify(formAttendees)) {
        apiData.attendees = formAttendees;
      }
    }
    
    onSave(apiData);
  };
  
  // Input field style
  const inputStyle = {
    width: '100%', 
    padding: '12px', 
    borderRadius: '8px', 
    border: '1px solid #e2e8f0',
    boxSizing: 'border-box',
    fontSize: '15px',
    transition: 'all 0.2s ease',
    backgroundColor: '#f8fafc'
  };
  
  // Label style
  const labelStyle = {
    display: 'block', 
    marginBottom: '8px', 
    fontWeight: 'bold',
    fontSize: '14px',
    color: '#4a5568'
  };
  
  // Form group style
  const formGroupStyle = {
    marginBottom: '20px'
  };
  
  return (
    <form onSubmit={handleSubmit} style={{
      padding: '36px 30px',
      backgroundColor: 'white',
      borderRadius: '16px',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <h2 style={{ 
        marginTop: '0', 
        marginBottom: '24px',
        fontSize: '22px',
        color: '#2d3748',
        fontWeight: '700'
      }}>
        {event ? 'Edit Event' : 'Create Event'}
      </h2>
      
      {/* Title */}
      <div style={formGroupStyle}>
        <label style={labelStyle}>Title*</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          placeholder="Enter event title"
          style={{
            ...inputStyle,
            fontSize: '16px'
          }}
        />
      </div>
      
      {/* Grid for date inputs on larger screens */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', 
        gap: '20px'
      }}>
        {/* Start time */}
        <div style={formGroupStyle}>
          <label style={labelStyle}>Start*</label>
          <input
            type="datetime-local"
            name="start"
            value={formData.start}
            onChange={handleChange}
            required
            style={inputStyle}
          />
        </div>
        
        {/* End time */}
        <div style={formGroupStyle}>
          <label style={labelStyle}>End*</label>
          <input
            type="datetime-local"
            name="end"
            value={formData.end}
            onChange={handleChange}
            required
            style={inputStyle}
          />
        </div>
      </div>
      
      {/* Description */}
      <div style={formGroupStyle}>
        <label style={labelStyle}>Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows="3"
          placeholder="Add a description (optional)"
          style={{
            ...inputStyle,
            resize: 'vertical',
            minHeight: '80px'
          }}
        />
      </div>
      
      {/* Add Guests */}
      <div style={formGroupStyle}>
        <label style={labelStyle}>Add Guests</label>
        <div style={{ 
          display: 'flex', 
          gap: '8px',
          marginBottom: '8px'
        }}>
          <input
            type="email"
            value={guestEmail}
            onChange={(e) => setGuestEmail(e.target.value)}
            onKeyPress={handleGuestEmailKeyPress}
            placeholder="Enter guest email address"
            style={{
              ...inputStyle,
              flex: 1
            }}
          />
          <button
            type="button"
            onClick={handleAddGuest}
            style={{
              padding: '12px 20px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#4285F4',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#3367d6';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#4285F4';
            }}
          >
            Add
          </button>
        </div>
        
        {/* Display organizer and attendees for existing events */}
        {event && (event.organizer || (event.attendees && event.attendees.length > 0)) && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{
              fontSize: '13px',
              color: '#64748b',
              marginBottom: '8px',
              fontWeight: '500'
            }}>
              Guests {event.attendees ? `(${event.attendees.length + (event.organizer ? 1 : 0)})` : (event.organizer ? '(1)' : '')}
            </div>
            
            {/* Show organizer */}
            {event.organizer && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                backgroundColor: '#fef3c7',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#92400e',
                marginBottom: '8px'
              }}>
                <span style={{ fontWeight: '600' }}>👑</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600' }}>
                    {event.organizer.displayName || event.organizer.email}
                    {event.organizer.email?.toLowerCase() === currentUserEmail?.toLowerCase() && 
                      <span style={{ marginLeft: '6px', fontSize: '11px', color: '#92400e' }}>(You)</span>
                    }
                  </div>
                  {event.organizer.displayName && (
                    <div style={{ fontSize: '11px', opacity: 0.8 }}>
                      {event.organizer.email}
                    </div>
                  )}
                </div>
                <span style={{ 
                  fontSize: '11px',
                  padding: '2px 6px',
                  backgroundColor: '#fbbf24',
                  borderRadius: '4px',
                  fontWeight: '600'
                }}>
                  Organizer
                </span>
              </div>
            )}
            
            {/* Show attendees */}
            {event.attendees && event.attendees.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {event.attendees.map((attendee, index) => {
                  const email = attendee.email || attendee;
                  const responseStatus = attendee.responseStatus || 'needsAction';
                  const isCurrentUser = email?.toLowerCase() === currentUserEmail?.toLowerCase();
                  
                  // Status badge styling
                  const statusStyles = {
                    'accepted': { bg: '#d1fae5', color: '#065f46', text: '✓ Yes' },
                    'declined': { bg: '#fee2e2', color: '#991b1b', text: '✗ No' },
                    'tentative': { bg: '#fef3c7', color: '#92400e', text: '? Maybe' },
                    'needsAction': { bg: '#e5e7eb', color: '#374151', text: '⋯ No response' }
                  };
                  const status = statusStyles[responseStatus] || statusStyles['needsAction'];
                  
                  return (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        backgroundColor: isCurrentUser ? '#dbeafe' : 'white',
                        borderRadius: '6px',
                        fontSize: '13px',
                        border: isCurrentUser ? '2px solid #3b82f6' : '1px solid #e5e7eb'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: isCurrentUser ? '600' : '400', color: '#1f2937' }}>
                          {attendee.displayName || email}
                          {isCurrentUser && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#3b82f6' }}>(You)</span>}
                        </div>
                        {attendee.displayName && (
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>
                            {email}
                          </div>
                        )}
                      </div>
                      <span style={{ 
                        fontSize: '11px',
                        padding: '4px 8px',
                        backgroundColor: status.bg,
                        color: status.color,
                        borderRadius: '4px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        {status.text}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Show response options if current user is an attendee (not the organizer) */}
            {event && currentUserEmail && 
             event.attendees && 
             event.attendees.some(a => (a.email || a)?.toLowerCase() === currentUserEmail?.toLowerCase()) &&
             event.organizer?.email?.toLowerCase() !== currentUserEmail?.toLowerCase() && (
              <div style={{
                marginTop: '12px',
                paddingTop: '12px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: '13px',
                  color: '#64748b',
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  Your Response:
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => handleUpdateResponse('accepted')}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: '#10b981',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
                  >
                    ✓ Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => handleUpdateResponse('declined')}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#dc2626'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#ef4444'}
                  >
                    ✗ No
                  </button>
                  <button
                    type="button"
                    onClick={() => handleUpdateResponse('tentative')}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: '#f59e0b',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#d97706'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#f59e0b'}
                  >
                    ? Maybe
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Display added guests for new events or when editing */}
        {(!event || formData.attendees.length > (event.attendees?.length || 0)) && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{
              fontSize: '13px',
              color: '#64748b',
              marginBottom: '8px',
              fontWeight: '500'
            }}>
              {event ? 'New Guests' : `Guests (${formData.attendees.length})`}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {formData.attendees
                .filter(a => !event || !event.attendees?.some(ea => (ea.email || ea) === (a.email || a)))
                .map((attendee, index) => {
                  const email = attendee.email || attendee;
                  return (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 10px',
                        backgroundColor: '#e0f2fe',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#0369a1'
                      }}
                    >
                      <span>{email}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveGuest(email)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#0369a1',
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          padding: '0',
                          marginLeft: '2px',
                          lineHeight: '1'
                        }}
                        title="Remove guest"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
      
      {/* Grid for location and color on larger screens */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', 
        gap: '20px'
      }}>
        {/* Location */}
        <div style={formGroupStyle}>
          <label style={labelStyle}>Location</label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleChange}
            placeholder="Add a location (optional)"
            style={inputStyle}
          />
        </div>
        
        {/* Recurrence */}
        <div style={formGroupStyle}>
          <label style={labelStyle}>Recurrence</label>
          <select
            name="recurrence"
            value={formData.recurrence}
            onChange={handleChange}
            style={inputStyle}
          >
            <option value="none">No Repeat</option>
            <option value="repeat">Repeat</option>
          </select>
        </div>
      </div>
      
      {/* Grid for color on larger screens */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', 
        gap: '20px'
      }}>
        {/* Color */}
        <div style={formGroupStyle}>
          <label style={labelStyle}>Color</label>
          
          {/* Custom color dropdown - completely reimplement */}
          <div style={{ position: 'relative' }}>
            {/* Custom dropdown with managed state */}
            <div 
              id="colorSelector"
              style={{
                ...inputStyle,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                backgroundImage: 'url("data:image/svg+xml;utf8,<svg fill=\'%236b7280\' height=\'24\' viewBox=\'0 0 24 24\' width=\'24\' xmlns=\'http://www.w3.org/2000/svg\'><path d=\'M7 10l5 5 5-5z\'/><path d=\'M0 0h24v24H0z\' fill=\'none\'/></svg>")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 10px center',
                backgroundSize: '20px'
              }}
              onClick={() => {
                // Toggle dropdown visibility on click
                const dropdown = document.getElementById('colorDropdown');
                if (dropdown) {
                  // If currently hidden, position it before showing
                  if (dropdown.style.display !== 'block') {
                    dropdown.style.display = 'block';
                    
                    // Position dropdown based on available space
                    const positionDropdown = () => {
                      const dropdown = document.getElementById('colorDropdown');
                      // Get dimensions and positions
                      const selectorRect = document.getElementById('colorSelector').getBoundingClientRect();
                      
                      if (!dropdown) return;
                      
                      // Get available space
                      const spaceBelow = window.innerHeight - selectorRect.bottom;
                      const spaceAbove = selectorRect.top;
                      
                      // Calculate minimum required height
                      const minHeight = 11 * 40; // 11 items * ~40px height per item
                      const dropdownHeight = Math.min(minHeight, 300); // cap at 300px
                      
                      // Position the dropdown
                      if (spaceBelow >= dropdownHeight + 10) {
                        // Show below - enough space
                        dropdown.style.top = `${selectorRect.bottom}px`;
                        dropdown.style.bottom = 'auto';
                        dropdown.style.left = `${selectorRect.left}px`;
                        dropdown.style.maxHeight = `${Math.min(spaceBelow - 20, 300)}px`;
                      } else if (spaceAbove >= dropdownHeight + 10) {
                        // Show above - not enough space below but enough above
                        dropdown.style.bottom = `${window.innerHeight - selectorRect.top}px`;
                        dropdown.style.top = 'auto';
                        dropdown.style.left = `${selectorRect.left}px`;
                        dropdown.style.maxHeight = `${Math.min(spaceAbove - 20, 300)}px`;
                      } else {
                        // Not enough space either way - center and make it smaller
                        dropdown.style.top = `${Math.max(10, window.innerHeight/2 - 150)}px`;
                        dropdown.style.left = `${selectorRect.left}px`;
                        dropdown.style.maxHeight = `${Math.min(window.innerHeight - 40, 300)}px`;
                      }
                    };
                    
                    // Run positioning after a small delay to allow the dropdown to render
                    setTimeout(positionDropdown, 0);
                  } else {
                    dropdown.style.display = 'none';
                  }
                }
              }}
            >
              <div style={{
                width: '18px',
                height: '18px',
                borderRadius: '4px',
                backgroundColor: GOOGLE_CALENDAR_COLORS[formData.colorId] || GOOGLE_CALENDAR_COLORS.default,
                border: '1px solid rgba(0,0,0,0.1)',
                marginRight: '10px',
                flexShrink: 0
              }} />
              <span style={{ color: '#4a5568' }}>
                {formData.colorId === '1' && 'Lavender'}
                {formData.colorId === '2' && 'Sage'}
                {formData.colorId === '3' && 'Grape'}
                {formData.colorId === '4' && 'Flamingo'}
                {formData.colorId === '5' && 'Banana'}
                {formData.colorId === '6' && 'Tangerine'}
                {formData.colorId === '7' && 'Peacock'}
                {formData.colorId === '8' && 'Graphite'}
                {formData.colorId === '9' && 'Blueberry'}
                {formData.colorId === '10' && 'Basil'}
                {formData.colorId === '11' && 'Tomato'}
              </span>
            </div>
            
            {/* Dropdown options - rendered in a portal */}
            <div 
              id="colorDropdown"
              style={{
                display: 'none',
                position: 'fixed', // Changed from absolute to fixed
                zIndex: 9999,      // Higher z-index to be above everything
                width: 'auto',     // Let it size to content
                minWidth: '220px', // Minimum width
                backgroundColor: 'white',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                borderRadius: '8px',
                maxHeight: '300px',
                overflowY: 'auto',
                marginTop: '4px'
              }}
            >
              {/* Map color options */}
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(key => {
                const colorKey = String(key);
                const color = GOOGLE_CALENDAR_COLORS[colorKey];
                const colorNames = {
                  '1': 'Lavender',
                  '2': 'Sage',
                  '3': 'Grape',
                  '4': 'Flamingo',
                  '5': 'Banana',
                  '6': 'Tangerine',
                  '7': 'Peacock',
                  '8': 'Graphite',
                  '9': 'Blueberry',
                  '10': 'Basil',
                  '11': 'Tomato'
                };
                const colorName = colorNames[colorKey];
                
                return (
                  <div 
                    key={colorKey}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 12px',
                      cursor: 'pointer',
                      backgroundColor: formData.colorId === colorKey ? '#f3f4f6' : 'transparent',
                      color: '#4a5568',
                      transition: 'all 0.15s ease',
                      borderRadius: '4px',
                      margin: '2px 4px'
                    }}
                    onClick={(e) => {
                      // Update the selected color
                      handleChange({ target: { name: 'colorId', value: colorKey }});
                      
                      // Hide the dropdown
                      document.getElementById('colorDropdown').style.display = 'none';
                      
                      // Prevent event bubbling
                      e.stopPropagation();
                    }}
                  >
                    <div style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '4px',
                      backgroundColor: color,
                      border: '1px solid rgba(0,0,0,0.1)',
                      marginRight: '10px',
                      flexShrink: 0
                    }} />
                    <span>{colorName}</span>
                  </div>
                );
              })}
            </div>
            
            {/* Hidden select for form data handling */}
            <select
              name="colorId"
              value={formData.colorId}
              onChange={handleChange}
              style={{ display: 'none' }}
            >
              <option value="1">Lavender</option>
              <option value="2">Sage</option>
              <option value="3">Grape</option>
              <option value="4">Flamingo</option>
              <option value="5">Banana</option>
              <option value="6">Tangerine</option>
              <option value="7">Peacock</option>
              <option value="8">Graphite</option>
              <option value="9">Blueberry</option>
              <option value="10">Basil</option>
              <option value="11">Tomato</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Recurrence options */}
      {formData.recurrence === 'repeat' && (
        <>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Frequency</label>
            <select
              name="frequency"
              value={formData.frequency}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Repeat every</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="number"
                name="interval"
                value={formData.interval}
                onChange={handleChange}
                min="1"
                max="99"
                style={{ ...inputStyle, width: '80px' }}
              />
              <span style={{ color: '#4a5568' }}>
                {formData.frequency === 'daily' && 'days'}
                {formData.frequency === 'weekly' && 'weeks'}
                {formData.frequency === 'monthly' && 'months'}
                {formData.frequency === 'yearly' && 'years'}
              </span>
            </div>
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>End repeat (optional)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="date"
                name="until"
                value={formData.until}
                onChange={handleChange}
                style={inputStyle}
              />
              <span style={{ color: '#718096', fontSize: '14px' }}>
                Leave blank to repeat indefinitely
              </span>
            </div>
          </div>
        </>
      )}
      
      {/* Notification Settings */}
      <div style={{ marginTop: '24px', marginBottom: '12px' }}>
        <h3 style={{ fontSize: '18px', color: '#2d3748', fontWeight: '600', marginBottom: '16px' }}>
          Notification Settings
        </h3>
        
        {/* Notification Toggle */}
        <div style={formGroupStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={labelStyle}>Enable Notifications</label>
            <label className="switch" style={{ 
              position: 'relative',
              display: 'inline-block',
              width: '60px',
              height: '34px'
            }}>
              <input
                type="checkbox"
                name="notifications_enabled"
                checked={formData.notifications_enabled}
                onChange={(e) => handleChange({ 
                  target: { 
                    name: 'notifications_enabled', 
                    value: e.target.checked
                  }
                })}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span style={{
                position: 'absolute',
                cursor: 'pointer',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: formData.notifications_enabled ? '#4285F4' : '#cbd5e0',
                transition: '.4s',
                borderRadius: '34px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <span style={{
                  position: 'absolute',
                  content: '""',
                  height: '26px',
                  width: '26px',
                  left: formData.notifications_enabled ? '30px' : '4px',
                  bottom: '4px',
                  backgroundColor: 'white',
                  transition: '.4s',
                  borderRadius: '50%',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}></span>
              </span>
            </label>
          </div>
        </div>
        
        {formData.notifications_enabled && (
          <>
            {/* Minutes Before */}
            <div style={formGroupStyle}>
              <label style={labelStyle}>Minutes Before Event</label>
              <input
                type="number"
                name="minutes_before"
                value={formData.minutes_before}
                onChange={handleChange}
                min="1"
                max="1440"
                style={inputStyle}
              />
            </div>
            
            {/* Repeat Notifications Toggle */}
            <div style={formGroupStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={labelStyle}>Repeat Notifications</label>
                <label className="switch" style={{ 
                  position: 'relative',
                  display: 'inline-block',
                  width: '60px',
                  height: '34px'
                }}>
                  <input
                    type="checkbox"
                    name="repeat_notifications"
                    checked={formData.repeat_notifications}
                    onChange={(e) => handleChange({ 
                      target: { 
                        name: 'repeat_notifications', 
                        value: e.target.checked
                      }
                    })}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: formData.repeat_notifications ? '#4285F4' : '#cbd5e0',
                    transition: '.4s',
                    borderRadius: '34px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <span style={{
                      position: 'absolute',
                      content: '""',
                      height: '26px',
                      width: '26px',
                      left: formData.repeat_notifications ? '30px' : '4px',
                      bottom: '4px',
                      backgroundColor: 'white',
                      transition: '.4s',
                      borderRadius: '50%',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}></span>
                  </span>
                </label>
              </div>
            </div>
            
            {/* Repeat Interval - Only shown when repeat is enabled */}
            {formData.repeat_notifications && (
              <div style={formGroupStyle}>
                <label style={labelStyle}>Repeat Interval (minutes)</label>
                <input
                  type="number"
                  name="repeat_interval"
                  value={formData.repeat_interval}
                  onChange={handleChange}
                  min="1"
                  max="60"
                  style={inputStyle}
                />
              </div>
            )}
            
            {/* Done Checkbox */}
            <div style={formGroupStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  name="done"
                  checked={formData.done}
                  onChange={(e) => handleChange({ 
                    target: { 
                      name: 'done', 
                      value: e.target.checked
                    }
                  })}
                  style={{ width: '18px', height: '18px' }}
                />
                <label style={{ fontSize: '15px', color: '#4a5568' }}>
                  Mark as done (stop further notifications)
                </label>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Buttons */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        marginTop: '30px',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          {/* Delete button (only for editing) */}
          {event && (
            <button
              type="button"
              onClick={() => onDelete(event.id)}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#fee2e2',
                color: '#b91c1c',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#fecaca';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#fee2e2';
              }}
            >
              Delete Event
            </button>
          )}
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#e5e7eb',
              color: '#4b5563',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#d1d5db';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#e5e7eb';
            }}
          >
            Cancel
          </button>
        </div>
        <button
          type="submit"
          style={{
            padding: '10px 24px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: '#4285F4',
            color: 'white',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#3367d6';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#4285F4';
          }}
        >
          {event ? 'Update Event' : 'Create Event'}
        </button>
      </div>
    </form>
  );
};

// Modal Component for Event Form
const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      backdropFilter: 'blur(4px)'
    }}
    onClick={(e) => {
      // Close modal when clicking the backdrop (outside the modal content)
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}
    >
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '95%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative',
        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)',
        animation: 'modalFadeIn 0.3s ease-out'
      }}
      onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: '#f3f4f6',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            zIndex: 10,
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#4b5563',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#e5e7eb';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#f3f4f6';
          }}
        >
          ×
        </button>
        {children}
      </div>
    </div>
  );
};

// Coordination Modal Component
const CoordinationModal = ({ isOpen, onClose }) => {
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingDuration, setMeetingDuration] = useState(60); // in minutes
  const [participantEmail, setParticipantEmail] = useState('');
  const [participants, setParticipants] = useState([]);
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');
  const [timePreference, setTimePreference] = useState('any'); // 'morning', 'afternoon', 'any'
  
  if (!isOpen) return null;
  
  // Handler for adding a participant
  const handleAddParticipant = () => {
    if (!participantEmail) return;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(participantEmail)) {
      alert('Please enter a valid email address');
      return;
    }
    
    // Check for duplicates
    if (participants.includes(participantEmail)) {
      alert('This email has already been added');
      return;
    }
    
    setParticipants(prev => [...prev, participantEmail]);
    setParticipantEmail('');
  };
  
  // Handler for removing a participant
  const handleRemoveParticipant = (emailToRemove) => {
    setParticipants(prev => prev.filter(email => email !== emailToRemove));
  };
  
  // Allow adding participant on Enter key
  const handleParticipantEmailKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddParticipant();
    }
  };
  
  // Handle coordination request submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (participants.length === 0) {
      alert('Please add at least one participant');
      return;
    }
    
    if (!dateRangeStart || !dateRangeEnd) {
      alert('Please specify a date range for the meeting');
      return;
    }
    
    const requestData = {
      meetingTitle,
      meetingDuration,
      participants,
      dateRangeStart,
      dateRangeEnd,
      timePreference
    };
    
    try {
      const response = await fetch(`${BASE_API_URL}/api/coordinate/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        credentials: 'include',
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to create coordination request');
      }
      
      const result = await response.json();
      alert('Coordination request sent! You will be notified when participants respond.');
      onClose();
      
      // Reset form
      setMeetingTitle('');
      setMeetingDuration(60);
      setParticipants([]);
      setDateRangeStart('');
      setDateRangeEnd('');
      setTimePreference('any');
      
    } catch (error) {
      console.error('Error creating coordination request:', error);
      alert('Failed to send coordination request. Please try again.');
    }
  };
  
  const inputStyle = {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    boxSizing: 'border-box',
    fontSize: '15px',
    transition: 'all 0.2s ease',
    backgroundColor: '#f8fafc'
  };
  
  const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 'bold',
    fontSize: '14px',
    color: '#4a5568'
  };
  
  const formGroupStyle = {
    marginBottom: '20px'
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      backdropFilter: 'blur(4px)'
    }}
    onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}
    >
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '95%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative',
        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)',
        animation: 'modalFadeIn 0.3s ease-out'
      }}
      onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: '#f3f4f6',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            zIndex: 10,
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#4b5563',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#e5e7eb';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#f3f4f6';
          }}
        >
          ×
        </button>
        
        <form onSubmit={handleSubmit} style={{
          padding: '36px 30px',
          backgroundColor: 'white',
          borderRadius: '16px',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <h2 style={{
            marginTop: '0',
            marginBottom: '24px',
            fontSize: '22px',
            color: '#2d3748',
            fontWeight: '700'
          }}>
            Coordinate Meeting
          </h2>
          
          {/* Meeting Title */}
          <div style={formGroupStyle}>
            <label style={labelStyle}>Meeting Title*</label>
            <input
              type="text"
              value={meetingTitle}
              onChange={(e) => setMeetingTitle(e.target.value)}
              required
              placeholder="Enter meeting title"
              style={{
                ...inputStyle,
                fontSize: '16px'
              }}
            />
          </div>
          
          {/* Meeting Duration */}
          <div style={formGroupStyle}>
            <label style={labelStyle}>Duration (minutes)*</label>
            <select
              value={meetingDuration}
              onChange={(e) => setMeetingDuration(Number(e.target.value))}
              style={inputStyle}
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
            </select>
          </div>
          
          {/* Date Range */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '20px'
          }}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>From Date*</label>
              <input
                type="date"
                value={dateRangeStart}
                onChange={(e) => setDateRangeStart(e.target.value)}
                required
                style={inputStyle}
              />
            </div>
            
            <div style={formGroupStyle}>
              <label style={labelStyle}>To Date*</label>
              <input
                type="date"
                value={dateRangeEnd}
                onChange={(e) => setDateRangeEnd(e.target.value)}
                required
                style={inputStyle}
              />
            </div>
          </div>
          
          {/* Time Preference */}
          <div style={formGroupStyle}>
            <label style={labelStyle}>Time Preference</label>
            <select
              value={timePreference}
              onChange={(e) => setTimePreference(e.target.value)}
              style={inputStyle}
            >
              <option value="any">Any time</option>
              <option value="morning">Morning (8 AM - 12 PM)</option>
              <option value="afternoon">Afternoon (12 PM - 5 PM)</option>
              <option value="evening">Evening (5 PM - 8 PM)</option>
            </select>
          </div>
          
          {/* Add Participants */}
          <div style={formGroupStyle}>
            <label style={labelStyle}>Add Participants</label>
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '8px'
            }}>
              <input
                type="email"
                value={participantEmail}
                onChange={(e) => setParticipantEmail(e.target.value)}
                onKeyPress={handleParticipantEmailKeyPress}
                placeholder="Enter participant email address"
                style={{
                  ...inputStyle,
                  flex: 1
                }}
              />
              <button
                type="button"
                onClick={handleAddParticipant}
                style={{
                  padding: '12px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#10b981',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#059669';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#10b981';
                }}
              >
                Add
              </button>
            </div>
            
            {/* Display added participants */}
            {participants.length > 0 && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{
                  fontSize: '13px',
                  color: '#64748b',
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  Participants ({participants.length})
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {participants.map((email, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 10px',
                        backgroundColor: '#d1fae5',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#065f46'
                      }}
                    >
                      <span>{email}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveParticipant(email)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#065f46',
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          padding: '0',
                          marginLeft: '2px',
                          lineHeight: '1'
                        }}
                        title="Remove participant"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Submit Button */}
          <div style={{
            display: 'flex',
            gap: '12px',
            marginTop: '24px'
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#e5e7eb',
                color: '#4b5563',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#d1d5db';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#e5e7eb';
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '10px 24px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#10b981',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                flex: 1
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#059669';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#10b981';
              }}
            >
              Send Coordination Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => {
  if (!isOpen) return null;
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      backdropFilter: 'blur(4px)'
    }}
    onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}
    >
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        width: '95%',
        maxWidth: '400px',
        position: 'relative',
        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)',
        animation: 'modalFadeIn 0.3s ease-out'
      }}
      onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ 
          margin: '0 0 16px 0',
          color: '#1a202c',
          fontSize: '18px',
          fontWeight: '600'
        }}>
          {title}
        </h3>
        
        <p style={{
          margin: '0 0 24px 0',
          color: '#4a5568',
          fontSize: '14px',
          lineHeight: '1.5'
        }}>
          {message}
        </p>
        
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              backgroundColor: 'white',
              color: '#4a5568',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#f8fafc';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'white';
            }}
          >
            Cancel
          </button>
          
          <button
            onClick={onConfirm}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#fee2e2',
              color: '#b91c1c',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#fecaca';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#fee2e2';
            }}
          >
            Delete Event
          </button>
        </div>
      </div>
    </div>
  );
};

// Format the assistant message content to render markdown-like formatting
const formatMessageContent = (content) => {
  if (!content) return '';
  
  // Handle CONTINUE_SEARCH responses specially
  if (content.startsWith('CONTINUE_SEARCH:')) {
    return content.substring('CONTINUE_SEARCH:'.length).trim();
  }
  
  // Remove the time zone statement
  content = content.replace(/These events are set to happen in your local time zone\./g, '');
  
  // Remove "- Time:" prefix while preserving the time information
  content = content.replace(/- Time:\s*/g, '');
  
  // First, let's parse the overall text structure
  // Split the text by numbered entries (1., 2., etc.)
  let entries = content.split(/(\d+\.\s+)/);
  
  // If there's an intro paragraph, preserve it
  let introParagraph = '';
  if (entries.length > 0 && !entries[0].match(/\d+\.\s+/)) {
    introParagraph = entries.shift();
  }
  
  // Rebuild the content with proper formatting
  let formattedContent = introParagraph;
  
  // Process each entry (number + content)
  for (let i = 0; i < entries.length; i += 2) {
    if (i + 1 < entries.length) {
      // Skip the number part (entries[i]), only use event content
      let eventContent = entries[i + 1]; // This is the event details
      
      // Format bold text
      eventContent = eventContent.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      
      // Format time ranges
      eventContent = eventContent.replace(/(\d{1,2}:\d{2}\s*(?:AM|PM))\s*to\s*(\d{1,2}:\d{2}\s*(?:AM|PM))/g, 
        '<span class="time-range">$1 to $2</span>');
      
      // Remove the stray "from" text that appears after the time range
      eventContent = eventContent.replace(/<\/span>\s*from/g, '</span>');
      
      // Remove any standalone "from" text
      eventContent = eventContent.replace(/\s*from\s*(?=\.|$)/g, '');
      eventContent = eventContent.replace(/\s*from\s+/g, ' ');
      
      // Remove extra newlines and trim the content
      eventContent = eventContent.trim().replace(/\n+/g, '\n');
      
      // Create the calendar entry card without the number and without extra spacing
      formattedContent += `<div class="calendar-entry" style="margin-bottom: 0;">${eventContent}</div>`;
    }
  }
  
  // Handle any remaining bold text
  formattedContent = formattedContent.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  
  // Convert line breaks (but not within calendar entries)
  formattedContent = formattedContent.replace(/\n(?!<div class="calendar-entry">)/g, '<br>');
  
  return formattedContent;
};

// AI Assistant Component
const CalendarAssistant = ({ events, isOpen, onClose, onRefresh, currentDate }) => {
  const [prompt, setPrompt] = useState("");
  const [response, setResponse] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [messageHistory, setMessageHistory] = useState([]);
  const promptRef = React.useRef(null);
  const responseContainerRef = React.useRef(null);

  const logToFile = async (data) => {
    try {
      await fetch(`${BASE_API_URL}/api/log-assistant-usage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        credentials: 'include',  // Add credentials to include cookies
      });
    } catch (error) {
      console.error('Error logging to file:', error);
    }
  };

  // Add this effect to scroll to bottom whenever messageHistory changes
  useEffect(() => {
    if (responseContainerRef.current) {
      responseContainerRef.current.scrollTop = responseContainerRef.current.scrollHeight;
    }
  }, [messageHistory]);

  const askAssistant = async (checkNextDay = false) => {
    if (!prompt.trim() && !checkNextDay) return;

    setIsLoading(true);
    
    try {
      // Check if this is a deletion request
      const isDeletion = prompt.toLowerCase().match(/\b(remove|delete|cancel)\b/);
      let finalPrompt = prompt;
      
      if (isDeletion) {
        // Extract the event title
        const eventTitle = prompt.toLowerCase()
          .replace(/\b(remove|delete|cancel|the|event|called|named)\b/g, '')
          .trim()
          .replace(/[."']/g, '')
          .trim();
          
        // Find matching event
        const matchingEvent = events.find(event => 
          event.title.toLowerCase() === eventTitle
        );
        
        // If found, append the event ID to the prompt
        if (matchingEvent) {
          finalPrompt = `${prompt} event_id:${matchingEvent.id}`;
        }
      }
      
      // Add user message to history first
      const userMessage = {
        role: 'user',
        content: prompt
      };
      
      setMessageHistory(prev => [...prev, userMessage]);
      
      // Clear input immediately after sending
      setPrompt('');
      
      // Send the user's question to the backend
      const timestamp = new Date().getTime();
      const response = await fetch(`${BASE_API_URL}/api/ask-assistant?t=${timestamp}&v=${APP_VERSION}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        body: JSON.stringify({ prompt: finalPrompt }),
        credentials: 'include'
      });
      
      // Handle authentication errors
      if (response.status === 401) {
        const errorData = await response.json();
        console.error("Authentication error:", errorData);
        
        // Show authentication error and login link
        const loginUrl = errorData.loginUrl || `${BASE_API_URL}/api/direct-login`;
        const loginMessage = `Authentication required. Please <a href="${loginUrl}" target="_blank">login again</a> and then try again.`;
        
        const errorMessageObj = {
          role: 'assistant',
          content: loginMessage
        };
        
        setMessageHistory(prev => [...prev, errorMessageObj]);
        setIsLoading(false);
        return;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Add AI response to history
      const aiMessage = {
        role: 'assistant',
        content: data.response || "No response content found"
      };
      
      setMessageHistory(prev => [...prev, aiMessage]);
      
      // Check if we need to refresh the calendar
      if (data.refresh === true) {
        onRefresh();
      }
      
      // Log assistant usage with the correct format
      await logToFile({
        prompt: userMessage.content,
        response: data.response,
        eventsCount: events.length,
        usage: data.usage || {
          completion_tokens: 0,
          prompt_tokens: 0,
          total_tokens: 0
        },
        success: true
      });
      
    } catch (error) {
      console.error('Error asking assistant:', error);
      
        const errorMessage = {
          role: 'assistant',
          content: `Error: ${error.message}. Please try again later.`
        };
        
        setMessageHistory(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      askAssistant();
    }
  };

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && promptRef.current) {
      setTimeout(() => {
        promptRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="assistant-overlay">
      <div className="assistant-container">
        <div className="assistant-header">
          <h2>Calendar Assistant</h2>
          {/* <h2>Calendar Assistant ({Intl.DateTimeFormat().resolvedOptions().timeZone})</h2> */}
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        
        <div className="assistant-content">
          <div 
            className="message-history" 
            ref={responseContainerRef}
          >
            {messageHistory.length === 0 ? (
              <div className="welcome-message">
                <p>Hello! I'm your calendar assistant. You can ask me about:</p>
                <ul>
                  <li>What's on your schedule today/tomorrow/next week</li>
                  <li>Finding specific events</li>
                  <li>Summarizing your upcoming appointments</li>
                  <li>Conflicts in your schedule</li>
                </ul>
              </div>
            ) : (
              messageHistory.map((msg, index) => (
                <div 
                  key={index} 
                  className={`message ${msg.role === 'user' ? 'user-message' : 'assistant-message'}`}
                >
                  {msg.role === 'user' ? (
                    <div className="message-content">{msg.content}</div>
                  ) : (
                    <div 
                      className="message-content formatted-response"
                      dangerouslySetInnerHTML={{ __html: formatMessageContent(msg.content) }}
                    />
                  )}
                </div>
              ))
            )}
            {isLoading && (
              <div className="assistant-message">
                <div className="loading-indicator">
                  <div className="loading-dot"></div>
                  <div className="loading-dot"></div>
                  <div className="loading-dot"></div>
                </div>
              </div>
            )}
          </div>
          
          <div className="assistant-input-container">
            <textarea
              ref={promptRef}
              className="assistant-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your calendar..."
              rows={3}
            />
            <button 
              onClick={() => askAssistant()} 
              className="assistant-send-btn"
              disabled={isLoading || !prompt.trim()}
            >
              Send
            </button>
          </div>
          
          <div className="assistant-shortcuts">
            <button 
              onClick={() => askAssistant(true)}
              className="shortcut-btn"
              disabled={isLoading}
            >
              What's tomorrow?
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Coordination Notifications Modal Component
const CoordinationNotificationsModal = ({ isOpen, onClose, requests, onRespond, onCreateNew, onViewProposals }) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
        padding: '20px',
        backdropFilter: 'blur(4px)'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '95%',
        maxWidth: '600px',
        maxHeight: '80vh',
        position: 'relative',
        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)',
        display: 'flex',
        flexDirection: 'column'
      }}
      onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: '#f3f4f6',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            zIndex: 10,
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#4b5563'
          }}
        >
          ×
        </button>
        
        <div style={{
          padding: '36px 30px 20px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h2 style={{
            marginTop: '0',
            marginBottom: '8px',
            fontSize: '22px',
            color: '#2d3748',
            fontWeight: '700'
          }}>
            Meeting Coordination Requests
          </h2>
          <p style={{
            margin: 0,
            color: '#6b7280',
            fontSize: '14px'
          }}>
            {requests.length} pending request{requests.length !== 1 ? 's' : ''}
          </p>
        </div>
        
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 30px'
        }}>
          {requests.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#6b7280'
            }}>
              <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                No pending coordination requests
              </p>
              <button
                onClick={onCreateNew}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Create New Request
              </button>
            </div>
          ) : (
            requests.map((request) => (
              <div
                key={request.id}
                style={{
                  backgroundColor: '#f9fafb',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '16px',
                  border: '1px solid #e5e7eb'
                }}
              >
                <h3 style={{
                  margin: '0 0 12px 0',
                  fontSize: '16px',
                  color: '#1f2937',
                  fontWeight: '600'
                }}>
                  {request.meeting_title}
                </h3>
                
                <div style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  marginBottom: '12px'
                }}>
                  <div style={{ marginBottom: '6px' }}>
                    <strong>Duration:</strong> {request.meeting_duration} minutes
                  </div>
                  <div style={{ marginBottom: '6px' }}>
                    <strong>Date Range:</strong> {new Date(request.date_range_start).toLocaleDateString()} - {new Date(request.date_range_end).toLocaleDateString()}
                  </div>
                  <div style={{ marginBottom: '6px' }}>
                    <strong>Participants:</strong> {request.all_participants ? request.all_participants.join(', ') : 'Loading...'}
                  </div>
                  <div>
                    <strong>Status:</strong> <span style={{
                      color: request.acceptance_status === 'pending' ? '#f59e0b' : 
                             request.status === 'proposals_ready' ? '#10b981' :
                             request.status === 'analyzing' ? '#3b82f6' : '#6b7280',
                      fontWeight: '600'
                    }}>
                      {request.acceptance_status === 'pending' ? 'Pending Your Response' : 
                       request.status === 'proposals_ready' ? 'Proposals Ready' :
                       request.status === 'analyzing' ? 'AI Analyzing...' :
                       request.status}
                    </span>
                  </div>
                </div>
                
                {request.acceptance_status === 'pending' && (
                  <div style={{
                    display: 'flex',
                    gap: '10px',
                    marginTop: '16px'
                  }}>
                    <button
                      onClick={() => onRespond(request.id, 'accepted')}
                      style={{
                        flex: 1,
                        padding: '10px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => onRespond(request.id, 'rejected')}
                      style={{
                        flex: 1,
                        padding: '10px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Reject
                    </button>
                  </div>
                )}
                
                {request.status === 'proposals_ready' && (
                  <div style={{
                    marginTop: '16px'
                  }}>
                    <button
                      onClick={() => {
                        onClose();
                        onViewProposals(request.id);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      View Proposals
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
        
        <div style={{
          padding: '20px 30px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={onCreateNew}
            style={{
              padding: '10px 20px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            + New Request
          </button>
          
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#e5e7eb',
              color: '#4b5563',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Proposals Modal Component
const ProposalsModal = ({ isOpen, onClose, proposalsData, onFinalize }) => {
  if (!isOpen || !proposalsData) return null;

  const { request, proposals } = proposalsData;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1002,
        padding: '20px',
        backdropFilter: 'blur(4px)'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '95%',
        maxWidth: '700px',
        maxHeight: '85vh',
        position: 'relative',
        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)',
        display: 'flex',
        flexDirection: 'column'
      }}
      onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: '#f3f4f6',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            zIndex: 10,
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#4b5563'
          }}
        >
          ×
        </button>
        
        <div style={{
          padding: '36px 30px 20px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h2 style={{
            marginTop: '0',
            marginBottom: '8px',
            fontSize: '22px',
            color: '#2d3748',
            fontWeight: '700'
          }}>
            {request.meeting_title}
          </h2>
          <p style={{
            margin: 0,
            color: '#6b7280',
            fontSize: '14px'
          }}>
            AI has analyzed all calendars and generated {proposals.length} proposal{proposals.length !== 1 ? 's' : ''}
          </p>
        </div>
        
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 30px'
        }}>
          {proposals.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#6b7280'
            }}>
              <p>No proposals available yet. AI is still analyzing calendars...</p>
            </div>
          ) : (
            proposals.map((proposal, index) => (
              <div
                key={proposal.id}
                style={{
                  backgroundColor: '#f9fafb',
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '16px',
                  border: '2px solid #e5e7eb',
                  position: 'relative'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  backgroundColor: index === 0 ? '#10b981' : '#6b7280',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {index === 0 ? 'Recommended' : `Option ${index + 1}`}
                </div>
                
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '18px',
                  color: '#1f2937',
                  fontWeight: '600'
                }}>
                  {new Date(proposal.proposed_start_time).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </h3>
                
                <div style={{
                  fontSize: '16px',
                  color: '#374151',
                  marginBottom: '16px',
                  fontWeight: '500'
                }}>
                  {new Date(proposal.proposed_start_time).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })} - {new Date(proposal.proposed_end_time).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}
                </div>
                
                <div style={{
                  backgroundColor: 'white',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    fontWeight: '600',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    AI Reasoning
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#4b5563',
                    lineHeight: '1.6'
                  }}>
                    {proposal.ai_reasoning}
                  </div>
                </div>
                
                <button
                  onClick={() => {
                    if (window.confirm(`Schedule meeting for ${new Date(proposal.proposed_start_time).toLocaleString()}?`)) {
                      onFinalize(proposal.id);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#059669';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#10b981';
                  }}
                >
                  Schedule This Time
                </button>
              </div>
            ))
          )}
        </div>
        
        <div style={{
          padding: '20px 30px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#e5e7eb',
              color: '#4b5563',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(Views.DAY);
  const [date, setDate] = useState(new Date());
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [slotInfo, setSlotInfo] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [coordinateModalOpen, setCoordinateModalOpen] = useState(false);
  const [pendingCoordinationRequests, setPendingCoordinationRequests] = useState([]);
  const [coordinationNotificationsOpen, setCoordinationNotificationsOpen] = useState(false);
  const [proposalsModalOpen, setProposalsModalOpen] = useState(false);
  const [currentProposals, setCurrentProposals] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  
  // Debug: Log when coordination requests change
  useEffect(() => {
    console.log("Pending coordination requests updated:", pendingCoordinationRequests.length, pendingCoordinationRequests);
  }, [pendingCoordinationRequests]);
  
  const [notificationPlatform, setNotificationPlatform] = useState(null);
  const [recurringEditModalOpen, setRecurringEditModalOpen] = useState(false);
  const [pendingEventChanges, setPendingEventChanges] = useState(null);

  // Force reload if cached version is outdated or detect Safari white screen
  useEffect(() => {
    // Detect Safari white screen issue - add this near the top of the component
    const detectWhiteScreen = () => {
      // Set a flag that we've at least tried to render
      sessionStorage.setItem('renderAttempted', 'true');
      
      // Check if this is an iOS device
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const isSafari = /Safari/i.test(navigator.userAgent) && 
                       !/Chrome/i.test(navigator.userAgent) &&
                       !/CriOS/i.test(navigator.userAgent);
      
      if (isIOS || isSafari) {
        console.log('iOS/Safari detected, applying special handling');
        
        // If content doesn't render within 2 seconds on iOS, force a hard reload
        const whiteScreenTimeout = setTimeout(() => {
          // Only do this check once per session to avoid reload loops
          if (!sessionStorage.getItem('safariForcedReload')) {
            const mainContent = document.querySelector('.rbc-calendar');
            if (!mainContent || mainContent.offsetHeight < 10) {
              console.log('White screen detected on iOS, forcing hard reload');
              sessionStorage.setItem('safariForcedReload', 'true');
              
              // Clear cache flags
              window._authCheckComplete = false;
              window._authState = undefined;
              window._authCheckInProgress = false;
              window._pushNotificationStatusCache = undefined;
              
              // Add cache-busting query parameter
              const url = new URL(window.location.href);
              url.searchParams.set('nocache', Date.now());
              window.location.href = url.toString();
            }
          }
        }, 2000);
        
        return () => clearTimeout(whiteScreenTimeout);
      }
    };
    
    // Run the white screen detection
    detectWhiteScreen();
    
    // Version-based cache busting
    const lastVersion = localStorage.getItem('appVersion');
    
    if (lastVersion !== APP_VERSION) {
      // Clear cache and store new version
      localStorage.setItem('appVersion', APP_VERSION);
      
      // Clear any cached data
      window._authCheckComplete = false;
      window._authState = undefined;
      window._authCheckInProgress = false;
      window._pushNotificationStatusCache = undefined;
      
      // If not the first load (when lastVersion is null), reload the page
      if (lastVersion) {
        console.log(`App updated from ${lastVersion} to ${APP_VERSION}, reloading...`);
        
        // Use a more aggressive approach for cache busting
        const url = new URL(window.location.href);
        url.searchParams.set('v', APP_VERSION);
        url.searchParams.set('t', Date.now()); // Add timestamp for extra cache busting
        window.location.href = url.toString();
      }
    }
  }, []);

  // Redirect non-www to www
  useEffect(() => {
    const host = window.location.host;
    if (host === 'promptlyyou.com') {
      window.location.href = `https://www.promptlyyou.com${window.location.pathname}${window.location.search}`;
    }
  }, []);

  // Check authentication status on page load
  useEffect(() => {
    // Check if we're coming from a logout
    const searchParams = new URLSearchParams(window.location.search);
    const fromLogout = searchParams.get('logout');
    
    if (fromLogout === 'true') {
      // Skip auth check and just show login screen
      setIsAuthenticated(false);
      setAuthChecked(true);
      // Clean up the URL
      if (window.history && window.history.replaceState) {
        window.history.replaceState({}, document.title, '/');
      }
      return;
    }
    
    // Prevent multiple simultaneous auth checks
    if (window._authCheckInProgress) {
      console.log("Auth check already in progress, waiting...");
      return;
    }
    
    // Set a global flag to track whether auth has been checked already
    if (window._authCheckComplete) {
      console.log("Auth already checked in this session, using cached result");
      // Use cached authentication state
      if (window._authState === true) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
      setAuthChecked(true);
      return;
    }
    
    // Set auth check in progress flag
    window._authCheckInProgress = true;
    
    // Set a timeout to handle cases where auth check takes too long
    const authTimeout = setTimeout(() => {
      if (!authChecked) {
        console.log("Auth check timed out - showing login screen");
        setAuthChecked(true);
        setIsAuthenticated(false);
        window._authCheckComplete = true;
        window._authCheckInProgress = false;
      }

    }, 15000); // 15 second timeout
    
    const checkAuth = async () => {
      try {
        console.log("Checking authentication status...");
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const response = await fetch(`${BASE_API_URL}/api/auth-status`, {
          credentials: 'include',
          cache: 'no-store',
          signal: controller.signal
          // Removed problematic headers that were causing CORS issues
        });
        
        clearTimeout(timeoutId);
        
        console.log("Auth status response code:", response.status);
        
        if (!response.ok) {
          console.error(`HTTP error ${response.status}`);
          setIsAuthenticated(false);
          setAuthChecked(true);
          window._authCheckComplete = true;
          window._authState = false;
          return;
        }
        
        const status = await response.json();
        console.log("Auth status response:", status);          if (status.authenticated) {
          console.log("User is authenticated, fetching events");
          setIsAuthenticated(true);
          setAuthChecked(true);
          window._authCheckComplete = true;
          window._authState = true;
          
          // Store user ID in localStorage
          if (status.user_id) {
            localStorage.setItem('userId', status.user_id);
            if (status.email) {
              localStorage.setItem('userEmail', status.email);
            }
            
            // Check platform and show appropriate notification prompt
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
            const isMacOS = /Macintosh/.test(navigator.userAgent);
            const isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
            
            // Set a flag to indicate login is in progress
            sessionStorage.setItem('loginInProgress', 'true');
            
            // Check for Android and show notification prompt if needed
            if (/Android/.test(navigator.userAgent) && 
                !localStorage.getItem('androidNotificationPromptPermanentlyDismissed') &&
                !sessionStorage.getItem('notificationPromptShownThisSession')) {
              
              console.log('Showing Android notification prompt at login');
              const notificationPromptEvent = new CustomEvent('showNotificationPrompt', {
                detail: { platform: 'android' }
              });
              window.dispatchEvent(notificationPromptEvent);
              // Mark that we've shown the prompt this session
              sessionStorage.setItem('notificationPromptShownThisSession', 'true');
            } else if ((isIOS || (isMacOS && isSafari)) && window.showIOSNotificationPrompt) {
              // Show prompt for iOS/macOS Safari users
              console.log('Checking iOS/macOS notification prompt conditions at login');
              window.showIOSNotificationPrompt();
            }
            
            // Clear the login in progress flag after a delay
            setTimeout(() => {
              sessionStorage.removeItem('loginInProgress');
            }, 5000);
            
            // ONE-TIME ONLY service worker and push notification setup after login
            // Set a session-wide flag to track if we've done the subscription check
            if ('serviceWorker' in navigator && !window._subscriptionCheckedOnce) {
              console.log('Initializing service worker and checking subscription - ONCE PER SESSION ONLY');
              window._subscriptionCheckedOnce = true;
              
              // Import the push notification module only once
              import('./utils/pushNotifications').then(module => {
                // Store the module globally so we don't need to import it again
                window._pushModule = module;
                
                // We'll check subscription status exactly once after login - with a delay 
                // to ensure the auth process is fully complete
                setTimeout(async () => {
                  try {
                    console.log('Checking push notification status once after login');
                    // This single call will check server status and update the cache
                    const isEnabled = await module.isPushNotificationEnabled();
                    console.log('Push notification status checked and cached as:', isEnabled);
                  } catch (err) {
                    console.error('Error checking push status:', err);
                  }
                }, 3000); // Short delay to ensure auth is complete
              }).catch(err => {
                console.error('Error loading push notification module:', err);
              });
            } else {
              console.log('Service worker not available, skipping push subscription setup');
            }
          }
          fetchEvents();
        } else {
          console.log("User is not authenticated");
          setIsAuthenticated(false);
          setAuthChecked(true);
          window._authCheckComplete = true;
          window._authState = false;
          localStorage.removeItem('userId');
          localStorage.removeItem('userEmail');
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.error("Auth check timed out");
        } else {
          console.error("Error checking auth status:", error);
        }
        setIsAuthenticated(false);
        setAuthChecked(true);
        window._authCheckComplete = true;
        window._authState = false;
        localStorage.removeItem('userId');
        localStorage.removeItem('userEmail');
      } finally {
        clearTimeout(authTimeout);
        // Reset auth check in progress flag
        window._authCheckInProgress = false;
      }
    };
    
    // Execute auth check once
    checkAuth();
    
    // Cleanup timeout if component unmounts
    return () => {
      clearTimeout(authTimeout);
      window._authCheckInProgress = false;
    };
  }, []);
  
  // Setup polling for authenticated users
  useEffect(() => {
    if (isAuthenticated) {
      console.log("Setting up polling interval for events and coordination requests");
      // Initial fetch
      fetchEvents();
      fetchPendingCoordinationRequests();
      
      // Set up polling every 5 minutes, but only refresh if no user activity
      const intervalId = setInterval(() => {
        // Only refresh if it's been more than 5 minutes since last user action
        const lastAction = localStorage.getItem('lastUserAction');
        const now = Date.now();
        if (!lastAction || (now - parseInt(lastAction)) > 5 * 60 * 1000) {
          console.log("Polling: Fetching events and coordination requests");
          fetchEvents();
          fetchPendingCoordinationRequests();
        }
      }, 5 * 60 * 1000);
      
      // Clean up interval on component unmount
      return () => clearInterval(intervalId);
    }
  }, [isAuthenticated]);

  // Set up listener for notification prompt events from notification-helper.js
  useEffect(() => {
    const handleNotificationPrompt = (event) => {
      console.log('Notification prompt event received:', event.detail);
      setNotificationPlatform(event.detail.platform);
      setShowNotificationPrompt(true);
    };
    
    window.addEventListener('showNotificationPrompt', handleNotificationPrompt);
    
    return () => {
      window.removeEventListener('showNotificationPrompt', handleNotificationPrompt);
    };
  }, []);

  // Track user actions
  useEffect(() => {
    const trackUserAction = () => {
      localStorage.setItem('lastUserAction', Date.now().toString());
    };

    // Add listeners for user interactions
    window.addEventListener('click', trackUserAction);
    window.addEventListener('keydown', trackUserAction);
    window.addEventListener('mousemove', trackUserAction);

    return () => {
      window.removeEventListener('click', trackUserAction);
      window.removeEventListener('keydown', trackUserAction);
      window.removeEventListener('mousemove', trackUserAction);
    };
  }, []);
  
  // Main event fetching function
  const fetchEvents = async (forceRefresh = false) => {
    setLoading(true);
    try {
      console.log("Fetching events...");
      
      // Only force refresh if explicitly requested
      const url = `${BASE_API_URL}/api/events${forceRefresh ? '?force=true' : ''}`;
      
      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      });
      
      console.log("Events response status:", response.status);
      
      if (response.status === 401) {
        console.log("Authentication required for events");
        setIsAuthenticated(false);
        setError("Authentication required. Please log in.");
        setLoading(false);
        return;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      
      const data = await response.json();
      console.log("Successfully received events:", data.length);
      
      if (!Array.isArray(data)) {
        throw new Error("Invalid response format");
      }
      
      const formatted = data.map((event) => ({
        ...event,
        start: new Date(event.start),
        end: new Date(event.end),
      }));
      
      setEvents(formatted);
      setError(null);
      setIsAuthenticated(true);
    } catch (error) {
      console.error("Error fetching events:", error);
      setError(error.message || "Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  // Fetch pending coordination requests
  const fetchPendingCoordinationRequests = async () => {
    try {
      console.log("Fetching pending coordination requests...");
      const response = await fetch(`${BASE_API_URL}/api/coordinate/pending`, {
        credentials: 'include'
      });
      
      console.log("Coordination requests response status:", response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log("Coordination requests data:", data);
        setPendingCoordinationRequests(data.requests || []);
        console.log("Set pending coordination requests count:", data.requests ? data.requests.length : 0);
      } else {
        console.error("Failed to fetch coordination requests:", response.status);
      }
    } catch (error) {
      console.error("Error fetching coordination requests:", error);
    }
  };

  // Fetch proposals for a coordination request
  const fetchProposals = async (requestId) => {
    try {
      const response = await fetch(`${BASE_API_URL}/api/coordinate/proposals/${requestId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentProposals(data);
        setProposalsModalOpen(true);
      } else {
        alert('Failed to fetch proposals');
      }
    } catch (error) {
      console.error("Error fetching proposals:", error);
      alert('Error fetching proposals');
    }
  };

  const handleLogin = () => {
    console.log("Login button clicked");
    // Use the same window for OAuth
    window.location.href = `${BASE_API_URL}/api/direct-login`;
  };
  
  const handleLogout = async () => {
    console.log("Logging out...");
    
    // Clear local state first
    setIsAuthenticated(false);
    setEvents([]);
    setShowNotificationPrompt(false); // Reset notification prompt state
    
    // Clear session-specific flags but keep permanent preferences
    // Clear auth and push notification cache
    window._authCheckComplete = false;
    window._authState = undefined;
    window._authCheckInProgress = false;
    window._pushNotificationStatusCache = undefined;
    
    // Make the logout request
    try {
      await fetch(`${BASE_API_URL}/api/logout`, {
        method: 'GET',
        credentials: 'include'  // Include cookies
      });
    } catch (error) {
      console.error('Error during logout:', error);
    }
    
    // Redirect to login page with a query parameter to indicate logout
    window.location.href = '/?logout=true';
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  const handleNavigate = (newDate) => {
    setDate(newDate);
  };

  // Handler for when an event is clicked
  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setModalOpen(true);
  };

  // Handler for when an empty slot is clicked (for creating a new event)
  const handleSelectSlot = (slotInfo) => {
    // Set the selected slot info with proper start and end times
    // For day/week view, the start time will be the exact time clicked
    // For month view, it defaults to the beginning of the day
    const startTime = slotInfo.start;
    
    // Create a end time 30 minutes after the start time
    const endTime = new Date(startTime);
    endTime.setMinutes(endTime.getMinutes() + 30);
    
    setSelectedEvent(null);
    setSlotInfo({
      ...slotInfo,
      start: startTime,
      end: endTime
    });
    setModalOpen(true);
  };

  // Handle saving an event (create or update)
  const handleSaveEvent = async (eventData) => {
    try {
      let response;
      
      // If this is an existing event
      if (selectedEvent) {
        // Check if it's a recurring event being modified
        if (isRecurringEvent(selectedEvent)) {
          // Store the pending changes and show the modal
          setPendingEventChanges(eventData);
          setRecurringEditModalOpen(true);
          return; // Stop here and wait for user choice
        } else {
          // Regular (non-recurring) event update
          response = await updateEvent(selectedEvent.id, eventData);
        }
      } else {
        // Creating a new event
        response = await createEvent(eventData);
      }
      
      // Check if we need to refresh based on server response
      if (response && response.refresh) {
        setModalOpen(false);
        try {
          await fetchEvents();
        } catch (error) {
          console.error('Error refreshing events after save:', error);
          setError('Failed to refresh calendar. Please try refreshing manually.');
        }
      }
    } catch (error) {
      console.error('Error saving event:', error);
      if (error.message === "Authentication required") {
        setIsAuthenticated(false);
      } else {
        setError(`Failed to ${selectedEvent ? 'update' : 'create'} event: ${error.message}`);
      }
    }
  };

  // Handle when the user makes a choice in the recurring event edit modal
  const handleRecurringEditChoice = async (choice) => {
    console.log('Handling recurring event choice:', choice);
    try {
      if (pendingEventChanges?.deleteOperation) {
        // Handle deletion of recurring event
        // Map 'following' → 'future' so the backend runs the proper
        // "delete this and all following" logic (not just a single-instance cancel)
        const deleteType = choice === 'following' ? 'future' : choice;
        const response = await deleteEvent(eventToDelete, deleteType);
        
        // Check if we need to refresh based on server response
        if (response && response.refresh) {
          setModalOpen(false);
          setDeleteConfirmOpen(false);
          try {
            await fetchEvents();
          } catch (error) {
            console.error('Error refreshing events after recurring delete:', error);
            setError('Failed to refresh calendar. Please try refreshing manually.');
          }
        }
      } else {
        // Handle update operation for recurring event
        let recurringEditType = null;
        switch (choice) {
          case 'this':
            recurringEditType = 'single';
            break;
          case 'following':
            recurringEditType = 'future';
            break;
          case 'all':
            recurringEditType = 'all';
            break;
          default:
            console.warn('Unexpected choice value:', choice);
            recurringEditType = 'single';
        }
        
        if (pendingEventChanges && selectedEvent) {
          console.log('Updating recurring event with type:', recurringEditType);
          const response = await updateEvent(selectedEvent.id, pendingEventChanges, recurringEditType);
          
          if (response && response.refresh) {
            setModalOpen(false);
            try {
              await fetchEvents();
            } catch (error) {
              console.error('Error refreshing events after recurring update:', error);
              setError('Failed to refresh calendar. Please try refreshing manually.');
            }
          }
        }
      }
      
      // Clear the modal and pending changes
      setRecurringEditModalOpen(false);
      setPendingEventChanges(null);
    } catch (error) {
      console.error('Error handling recurring event action:', error);
      setError(`Failed to ${pendingEventChanges?.deleteOperation ? 'delete' : 'update'} recurring event: ${error.message}`);
      setPendingEventChanges(null);
    }
  };

  // Handle deleting an event
  const handleDeleteEvent = async (eventId) => {
    // Store the event ID for later use
    setEventToDelete(eventId);
    
    // Get the event details to check if it's recurring
    const eventToDelete = events.find(event => event.id === eventId);
    
    if (isRecurringEvent(eventToDelete)) {
      // If it's recurring, show the recurring edit modal instead of the delete confirmation
      setPendingEventChanges({ deleteOperation: true });
      setRecurringEditModalOpen(true);
    } else {
      // Regular deletion with confirmation
      setDeleteConfirmOpen(true);
    }
  };

  // Handle actual deletion after confirmation
  const handleConfirmDelete = async () => {
    try {
      const response = await deleteEvent(eventToDelete);
      
      // Check if we need to refresh based on server response
      if (response && response.refresh) {
        setModalOpen(false);
        setDeleteConfirmOpen(false);
        try {
          await fetchEvents();
        } catch (error) {
          console.error('Error refreshing events after delete:', error);
          setError('Failed to refresh calendar. Please try refreshing manually.');
        }
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      if (error.message === "Authentication required") {
        setIsAuthenticated(false);
      } else {
        setError(`Failed to delete event: ${error.message}`);
      }
    }
  };

  // If auth status hasn't been checked yet, show loading
  if (!authChecked) {
    // Check if this is an iOS device that might be experiencing white screen issues
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /Safari/i.test(navigator.userAgent) && 
                    !/Chrome/i.test(navigator.userAgent) &&
                    !/CriOS/i.test(navigator.userAgent);
    
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        <div style={{ marginBottom: '20px' }}>Loading...</div>
        <div style={{ fontSize: '14px', maxWidth: '400px', textAlign: 'center', color: '#999' }}>
          If loading takes too long, you can try <button 
            onClick={() => {
              // Clear all auth and notification flags to force a complete reset
              window._authCheckComplete = false;
              window._authState = undefined;
              window._authCheckInProgress = false;
              window._pushNotificationStatusCache = undefined;
              window._reconnectionInProgress = false;
              window._lastServerSubscriptionCheck = 0;
              
              // For iOS/Safari, use a special reload approach
              if (isIOS || isSafari) {
                console.log('Using iOS/Safari specific reload with cache clearing');
                // Add cache-busting parameters
                const url = new URL(window.location.href);
                url.searchParams.set('nocache', Date.now());
                window.location.href = url.toString();
              } else {
                // Regular reload for other browsers
                window.location.reload();
              }
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#3b82f6',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '14px',
              padding: 0
            }}
          >refreshing the page</button>
        </div>
        {(isIOS || isSafari) && (
          <div style={{ 
            fontSize: '14px', 
            maxWidth: '400px', 
            textAlign: 'center', 
            color: '#999',
            marginTop: '10px', 
            padding: '8px', 
            backgroundColor: '#f9fafb',
            borderRadius: '8px'
          }}>
            <strong>iOS/Safari users:</strong> If you see a white screen, try closing all Safari tabs and reopening the app, or try in private browsing mode.
          </div>
        )}
      </div>
    );
  }

  // If not authenticated, show login panel
  if (!isAuthenticated) {
    return <LoginPanel onLogin={handleLogin} />;
  }

  return (
    <>
      {/* Install Prompt Modal - Disabled */}
      {/* <InstallPrompt /> */}
      
      {/* OS-specific Notification Prompt Modal */}
      <NotificationPrompt
        isOpen={showNotificationPrompt}
        platform={notificationPlatform}
        onClose={() => {
          // Just close the modal and set session flag
          setShowNotificationPrompt(false);
          // Set a session flag to prevent reopening during this session
          sessionStorage.setItem('notificationPromptShownThisSession', 'true');
        }}
        onDismiss={() => {
          // Add the permanent flag when clicking "Don't show again"
          setShowNotificationPrompt(false);
          if (notificationPlatform === 'ios') {
            localStorage.setItem('iosNotificationPromptPermanentlyDismissed', 'true');
          } else if (notificationPlatform === 'android') {
            localStorage.setItem('androidNotificationPromptPermanentlyDismissed', 'true');
          }
          // Also set session flag
          sessionStorage.setItem('notificationPromptShownThisSession', 'true');
        }}
      />
      
      <div style={{ 
        height: "100vh", 
        padding: "0px 12px", // Reduced side padding to match the calendar
        backgroundColor: "#0B1420", 
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        display: "flex",
        flexDirection: "column"
      }}>
      <style>
        {`
          @media (max-width: 768px) {
            .rbc-calendar {
              font-size: 11px;
              padding: 0 !important;
            }
            
            /* Mobile top controls layout */
            .mobile-top-controls {
              display: flex !important;
              flex-direction: column !important;
              gap: 8px !important;
              width: 100% !important;
              margin-bottom: 16px !important;
              padding: 0 8px !important;
            }
            
            .mobile-top-controls > div {
              display: grid !important;
              grid-template-columns: 1fr 1fr !important;
              gap: 8px !important;
              width: 100% !important;
            }
            
            .mobile-top-controls button {
              height: 44px !important;
              min-height: 44px !important;
              max-height: 44px !important;
              white-space: nowrap !important;
              justify-content: center !important;
              width: 100% !important;
              padding: 0 12px !important;
              font-size: 14px !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
            }

            /* Hide desktop controls */
            .desktop-toolbar-buttons {
              display: none !important;
            }

            /* Adjust logo size and position on mobile */
            .app-logo {
              height: 50px !important;
              margin: 4px 0 !important;
              margin-left: -10px !important;
            }
            
            /* Adjust the logo container on mobile */
            .logo-container {
              padding-top: 4px !important;
              margin-bottom: 4px !important;
            }
          }
          
          @media (min-width: 769px) {
            .mobile-top-controls {
              display: none !important;
            }
            .desktop-toolbar-buttons {
              display: flex !important;
              gap: 12px !important;
            }
          }
        `}
      </style>
      <div className="logo-container" style={{
        display: "flex",
        alignItems: "center",
        marginBottom: "12px",
        paddingTop: "0px",
        marginLeft: "-8px" // Changed from 4px to -8px to align with calendar
      }}>
        <img 
          src="PromptlyYou_Logo.png" 
          alt="PromptlyYou Logo" 
          className="app-logo"
          style={{
            height: "80px",
            marginRight: "12px",
            marginTop: "12px",
            marginLeft: "-20px", // Changed from -4px to 0px
            backgroundColor: "transparent",
            borderRadius: "8px",
          }}
        />
        <div className="desktop-toolbar-buttons" style={{ marginLeft: 'auto' }}>
          <NotificationSettings />
          <button
            onClick={() => fetchEvents(true)}
            disabled={loading}
            style={{
              padding: '10px 18px',
              cursor: loading ? 'wait' : 'pointer',
              backgroundColor: '#4285F4',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(66, 133, 244, 0.25)',
              width: '150px',
            }}
            onMouseOver={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#3367d6';
                e.target.style.boxShadow = '0 4px 12px rgba(66, 133, 244, 0.3)';
              }
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#4285F4';
              e.target.style.boxShadow = '0 2px 8px rgba(66, 133, 244, 0.25)';
            }}
          >
            {loading ? 'Refreshing...' : 'Refresh Calendar'}
          </button>

          <button
            onClick={() => setAssistantOpen(!assistantOpen)}
            style={{
              padding: '10px 18px',
              backgroundColor: assistantOpen ? '#3b82f6' : '#e2e8f0',
              color: assistantOpen ? 'white' : '#4a5568',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              width: '150px',
            }}
          >
            {assistantOpen ? 'Hide AI Assistant' : 'AI Assistant'}
          </button>
          
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => {
                if (pendingCoordinationRequests.length > 0) {
                  setCoordinationNotificationsOpen(true);
                } else {
                  setCoordinateModalOpen(true);
                }
              }}
              style={{
                padding: '10px 18px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer',
                width: '150px',
              }}
            >
              Coordinate
            </button>
            {pendingCoordinationRequests.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                backgroundColor: '#ef4444',
                color: 'white',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
              }}>
                {pendingCoordinationRequests.length}
              </div>
            )}
          </div>
          
          {/* Logout button commented out
          <button
            onClick={handleLogout}
            style={{
              padding: '10px 18px',
              backgroundColor: '#fee2e2',
              color: '#b91c1c',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '500',
              cursor: 'pointer',
              width: '150px',
            }}
          >
            Logout
          </button>
          */}
        </div>
      </div>
      {/* Mobile controls */}
      <div className="mobile-top-controls">
        <div>
          <NotificationSettings />
          <button 
            className="refresh-button"
            onClick={() => fetchEvents(true)} 
            disabled={loading}
            style={{ 
              backgroundColor: '#4285F4',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: loading ? 'wait' : 'pointer'
            }}
          >
            {loading ? 'Refreshing...' : 'Refresh Calendar'}
          </button>
        </div>
        
        <div>
          <button
            onClick={() => setAssistantOpen(!assistantOpen)}
            style={{
              backgroundColor: assistantOpen ? '#3b82f6' : '#e2e8f0',
              color: assistantOpen ? 'white' : '#4a5568',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {assistantOpen ? 'Hide AI Assistant' : 'AI Assistant'}
          </button>
          
          <div style={{ position: 'relative', display: 'inline-block', marginLeft: '8px' }}>
            <button
              onClick={() => {
                if (pendingCoordinationRequests.length > 0) {
                  setCoordinationNotificationsOpen(true);
                } else {
                  setCoordinateModalOpen(true);
                }
              }}
              style={{
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Coordinate
            </button>
            {pendingCoordinationRequests.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                backgroundColor: '#ef4444',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: 'bold',
              }}>
                {pendingCoordinationRequests.length}
              </div>
            )}
          </div>
          
          {/* Mobile Logout button commented out
          <button
            onClick={handleLogout}
            style={{
              backgroundColor: '#fee2e2',
              color: '#b91c1c',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Logout
          </button>
          */}
        </div>
      </div>
      {error && (
        <div style={{ 
          color: '#e53e3e', 
          marginBottom: 24,
          padding: 16,
          backgroundColor: '#FEF2F2',
          borderRadius: 8,
          borderLeft: '4px solid #F87171',
          whiteSpace: 'pre-wrap',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <strong>Error:</strong> {error}
          <p style={{ fontSize: 14, marginTop: 8, color: '#7F1D1D' }}>
            Try refreshing again. If the problem persists, the server might be down.
          </p>
        </div>
      )}
      {/* <div style={{
        backgroundColor: "white", 
        borderRadius: "16px", 
        overflow: "hidden",
        boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
        flex: 1,
        marginBottom: "12px" // Reduced from 24px to 12px
      }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: "calc(100vh - 140px)" }} // Adjusted from 160px to 140px
          views={allViews}
          view={view}
          onView={handleViewChange}
          date={date}
          onNavigate={handleNavigate}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          selectable={true}
          scrollToTime={new Date().setHours(new Date().getHours() - 2)}
          components={{
            event: EventComponent,
            toolbar: (toolbarProps) => (
              <CustomToolbar 
                {...toolbarProps}
              />
            )
          }}
          min={new Date(0, 0, 0, 0, 0, 0)} // Set minimum time to 12:00 AM
          max={new Date(0, 0, 0, 23, 59, 59)} // Set maximum time to 11:59 PM
        />
      </div> */}

      {/* Calendar Container: fills remaining space and scrolls internally */}
      <div style={{
        backgroundColor: "white",
        borderRadius: "16px",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflowY: "auto",
        marginBottom: "12px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.06)"
      }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: "100%" }}          // ← fill its parent
          views={allViews}
          view={view}
          onView={handleViewChange}
          date={date}
          onNavigate={handleNavigate}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          selectable={true}
          scrollToTime={new Date().setHours(new Date().getHours() - 2)}
          components={{
            event: EventComponent,
            toolbar: (toolbarProps) => <CustomToolbar {...toolbarProps} />
          }}
          min={new Date(0, 0, 0, 0, 0, 0)}      // 12:00 AM
          max={new Date(0, 0, 0, 23, 59, 59)}  // 11:59 PM
        />
      </div>


      
      {/* Modal for Creating/Editing Events */}
      <Modal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
      >
        <EventForm 
          event={selectedEvent}
          onSave={handleSaveEvent}
          onCancel={() => setModalOpen(false)}
          onDelete={handleDeleteEvent}
          slotInfo={slotInfo}
        />
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Event"
        message="Are you sure you want to delete this event? This action cannot be undone."
      />
      
      {/* Recurring Event Edit Modal */}
      <RecurringEventEditModal
        isOpen={recurringEditModalOpen}
        onClose={() => {
          setRecurringEditModalOpen(false);
          setPendingEventChanges(null);
        }}
        onEditChoice={handleRecurringEditChoice}
        isDelete={pendingEventChanges && pendingEventChanges.deleteOperation}
      />
      
      {/* Coordination Modal */}
      <CoordinationModal
        isOpen={coordinateModalOpen}
        onClose={() => setCoordinateModalOpen(false)}
      />
      
      {/* Coordination Notifications Modal */}
      <CoordinationNotificationsModal
        isOpen={coordinationNotificationsOpen}
        onClose={() => setCoordinationNotificationsOpen(false)}
        requests={pendingCoordinationRequests}
        onRespond={async (requestId, response) => {
          try {
            let availabilityData = null;
            
            // If accepting, fetch calendar and extract busy times
            if (response === 'accepted') {
              // Find the request to get date range
              const request = pendingCoordinationRequests.find(r => r.id === requestId);
              if (!request) {
                alert('Could not find request details');
                return;
              }
              
              try {
                // Fetch user's calendar events for the date range
                const calendarRes = await fetch(
                  `${BASE_API_URL}/api/events?start=${request.date_range_start}&end=${request.date_range_end}`,
                  { credentials: 'include' }
                );
                
                if (!calendarRes.ok) {
                  throw new Error('Failed to fetch calendar');
                }
                
                const calendarEvents = await calendarRes.json();
                
                // Extract busy times
                const busyTimes = calendarEvents.map(event => ({
                  start: event.start,
                  end: event.end
                }));
                
                availabilityData = {
                  busy: busyTimes,
                  dateRangeStart: request.date_range_start,
                  dateRangeEnd: request.date_range_end
                };
                
                console.log('Sending availability data:', availabilityData);
              } catch (calError) {
                console.error('Error fetching calendar for availability:', calError);
                alert('Failed to fetch your calendar. Please try again.');
                return;
              }
            }
            
            const res = await fetch(`${BASE_API_URL}/api/coordinate/respond`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ 
                requestId, 
                response,
                availabilityData 
              })
            });
            
            if (res.ok) {
              // Refresh pending requests
              await fetchPendingCoordinationRequests();
              const data = await res.json();
              
              if (data.all_responded && data.all_accepted) {
                alert('All participants accepted! AI is analyzing calendars to find optimal meeting times.');
              }
            } else {
              alert('Failed to respond to coordination request');
            }
          } catch (error) {
            console.error('Error responding to coordination request:', error);
            alert('Error responding to coordination request');
          }
        }}
        onCreateNew={() => {
          setCoordinationNotificationsOpen(false);
          setCoordinateModalOpen(true);
        }}
        onViewProposals={fetchProposals}
      />
      
      {/* Proposals Review Modal */}
      <ProposalsModal
        isOpen={proposalsModalOpen}
        onClose={() => {
          setProposalsModalOpen(false);
          setCurrentProposals(null);
        }}
        proposalsData={currentProposals}
        onFinalize={async (proposalId) => {
          try {
            const response = await fetch(`${BASE_API_URL}/api/coordinate/finalize`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ proposalId })
            });
            
            if (response.ok) {
              const data = await response.json();
              alert(`Meeting scheduled successfully! Created ${data.created_events.length} calendar event(s).`);
              setProposalsModalOpen(false);
              setCurrentProposals(null);
              await fetchEvents(); // Refresh calendar
              await fetchPendingCoordinationRequests(); // Refresh coordination requests
            } else {
              alert('Failed to finalize meeting');
            }
          } catch (error) {
            console.error('Error finalizing meeting:', error);
            alert('Error finalizing meeting');
          }
        }}
      />
      
      {/* AI Assistant */}
      <CalendarAssistant
        events={events}
        isOpen={assistantOpen}
        onClose={() => setAssistantOpen(false)}
        onRefresh={fetchEvents}
        currentDate={date}
      />
    </div>
    </>
  );
}

export default App;